import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components/macro';
import theme from 'styled-theming';
import {ClickAwayListener} from '@mui/base/ClickAwayListener';
import ClinicAvatar from '@components/ClinicAvatar';
import {useAuth} from '@hooks/useAuth';
import {formatClinicLabel} from '@utils/tenant';
import {colors, dark, light, textSizes, effects, flex} from '@styles/vars';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 320px;
  position: relative;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid ${theme('theme', {
    light: light.border,
    dark: dark.border
  })};
  background: ${theme('theme', {
    light: light.widgetBg,
    dark: dark.widgetBg
  })};
  transition: border-color var(--transition), box-shadow var(--transition), background-color var(--transition);

  &:hover,
  &:focus {
    border-color: rgba(58, 124, 165, 0.45);
    box-shadow: 0 8px 18px rgba(7, 18, 30, 0.08);
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
`;

const TriggerText = styled.span`
  ${flex.col};
  align-items: flex-start;
  min-width: 0;
  text-align: left;
`;

const TriggerLabel = styled.span`
  font-size: ${textSizes['10']};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme('theme', {
    light: colors.secondary,
    dark: dark.text
  })};
  opacity: 0.85;
`;

const TriggerName = styled.span`
  font-size: ${textSizes['14']};
  font-weight: 600;
  color: ${theme('theme', {
    light: colors.primary,
    dark: dark.text
  })};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`;

const Chevron = styled.span`
  flex-shrink: 0;
  color: ${theme('theme', {
    light: colors.secondary,
    dark: dark.text
  })};

  i {
    font-size: 12px;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  padding: 8px;
  border-radius: 10px;
  ${flex.col};
  gap: 4px;
  box-shadow: ${effects.widgetShadow};
  transition: var(--transition);
  opacity: 0;
  visibility: hidden;
  min-width: 240px;
  width: max-content;
  max-width: min(320px, 90vw);
  background-color: ${theme('theme', {
    light: light.widgetBg,
    dark: dark.widgetBg
  })};
  z-index: 10001;

  &.visible {
    opacity: 1;
    visibility: visible;
  }
`;

const MenuTitle = styled.span`
  padding: 6px 10px 4px;
  font-size: ${textSizes['10']};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme('theme', {
    light: colors.secondary,
    dark: dark.text
  })};
`;

const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 8px;
  text-align: left;

  &[data-active='true'] {
    background: rgba(58, 124, 165, 0.1);
  }

  &:hover,
  &:focus {
    background: rgba(58, 124, 165, 0.08);
  }

  .name {
    flex: 1;
    min-width: 0;
    font-size: ${textSizes['14']};
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .badge {
    font-size: ${textSizes['10']};
    font-weight: 700;
    text-transform: uppercase;
    color: ${colors.blue};
  }
`;

const MenuDivider = styled.hr`
  width: 100%;
  border: 0;
  border-top: 1px solid ${theme('theme', {
    light: light.border,
    dark: dark.border
  })};
  margin: 4px 0;
`;

const ClinicSwitcher = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [switchingId, setSwitchingId] = useState('');
    const {
        tenantId,
        tenantName,
        availableClinics,
        isSuperAdmin,
        selectClinic
    } = useAuth();

    const canSwitch = isSuperAdmin || availableClinics.length > 1;

    if (!canSwitch) {
        return null;
    }

    const handleQuickSwitch = async clinicId => {
        if (!clinicId || clinicId === tenantId) {
            setOpen(false);
            return;
        }

        setSwitchingId(clinicId);

        try {
            await selectClinic(clinicId);
            setOpen(false);
            navigate('/dashboard_a', {replace: true});
        } catch (error) {
            console.error('Error al cambiar de clínica:', error);
        } finally {
            setSwitchingId('');
        }
    };

    const handleOpenSelector = () => {
        setOpen(false);
        navigate('/select-clinic');
    };

    const currentLabel = tenantName || 'Sin clínica activa';
    const isBusy = Boolean(switchingId);

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Wrapper>
                <Trigger
                    type="button"
                    onClick={() => setOpen(prev => !prev)}
                    aria-label="Cambiar clínica activa"
                    aria-expanded={open}
                    disabled={isBusy}
                >
                    <ClinicAvatar name={currentLabel} size={30} />
                    <TriggerText>
                        <TriggerLabel>Clínica actual</TriggerLabel>
                        <TriggerName title={currentLabel}>{currentLabel}</TriggerName>
                    </TriggerText>
                    <Chevron aria-hidden="true">
                        <i className="icon icon-chevron-down" />
                    </Chevron>
                </Trigger>

                <Menu className={open ? 'visible' : ''}>
                    <MenuTitle>Cambiar entorno</MenuTitle>
                    {availableClinics.map(clinic => {
                        const label = formatClinicLabel(clinic.id, clinic.nombre);
                        const isActive = clinic.id === tenantId;
                        const isSubmitting = switchingId === clinic.id;

                        return (
                            <MenuItem
                                key={clinic.id}
                                type="button"
                                data-active={isActive ? 'true' : 'false'}
                                onClick={() => handleQuickSwitch(clinic.id)}
                                disabled={isBusy}
                            >
                                <ClinicAvatar name={label} size={28} />
                                <span className="name">{label}</span>
                                <span className="badge">
                                    {isSubmitting ? '...' : isActive ? 'Actual' : 'Usar'}
                                </span>
                            </MenuItem>
                        );
                    })}
                    <MenuDivider />
                    <MenuItem type="button" onClick={handleOpenSelector} disabled={isBusy}>
                        <i className="icon icon-blocks" aria-hidden="true" />
                        <span className="name">Ver todas las clínicas</span>
                    </MenuItem>
                </Menu>
            </Wrapper>
        </ClickAwayListener>
    );
};

export default ClinicSwitcher;
